/**
 * Canonical registry of pages editable via the Puck editor.
 *
 * Adding or removing entries here automatically surfaces/hides them in
 * the admin quick-edit FAB menu — no component changes needed.
 */

export interface EditablePage {
  /** URL-safe identifier used in `/admin/pages/{slug}/edit` */
  slug: string
  /** Human-readable name shown in the FAB menu */
  label: string
  /** The public route to view this page */
  viewPath: string
}

/**
 * Single source of truth for all Puck-editable pages.
 * To add a page, append an entry here — the FAB picks it up automatically.
 */
export const EDITABLE_PAGES: readonly EditablePage[] = [
  { slug: 'homepage', label: 'Homepage', viewPath: '/' },
  { slug: 'learning-path', label: 'Learning Path', viewPath: '/learning-path' },
  { slug: 'sign-in', label: 'Sign In', viewPath: '/sign-in' },
  { slug: 'sign-up', label: 'Sign Up', viewPath: '/sign-up' },
  { slug: 'forgot-password', label: 'Forgot Password', viewPath: '/auth/forgot-password' },
  { slug: 'email-verification', label: 'Email Verification', viewPath: '/auth/email-verification' },
  { slug: 'verify-account', label: 'Verify Account', viewPath: '/auth/verify-account' },
  { slug: 'reset-password', label: 'Reset Password', viewPath: '/auth/reset-password' },
  {
    slug: 'password-reset-successful',
    label: 'Password Reset Success',
    viewPath: '/auth/password-reset-successful',
  },
] as const

/**
 * Look up a single editable page by its slug.
 * Returns `undefined` when the slug is not registered.
 */
export function findEditablePage(slug: string): EditablePage | undefined {
  return EDITABLE_PAGES.find(p => p.slug === slug)
}

/**
 * Build the admin edit route for a given page slug.
 * Follows the convention `/admin/pages/{slug}/edit`.
 */
export function getEditPath(slug: string): string {
  return `/admin/pages/${slug}/edit`
}
